import { fileUrl } from "@/lib/api";
import { BrandIcon } from "@/lib/icons";
import { useState } from "react";
import { ChevronRight, ChevronLeft, Languages, Heart } from "lucide-react";

// Reads a custom uploaded icon path OR falls back to a brand icon by name.
function ButtonIcon({ btn, size = 32 }) {
    if (btn.icon_url) {
        return (
            <img
                src={fileUrl(btn.icon_url)}
                alt=""
                className="h-full w-full object-contain"
                style={{ maxHeight: size, maxWidth: size }}
                draggable={false}
            />
        );
    }
    return <BrandIcon name={btn.icon} size={size} />;
}

// Public NFC business page — hero + rounded-card button list matching reference.
export default function PublicPageView({ business, forceLang, insideFrame = false }) {
    const b = business || {};
    const initial = forceLang || b.default_lang || "en";
    const [lang, setLang] = useState(initial);
    const isRtl = lang === "ar";

    // Localized text with fallback: hero_* falls back to name/welcome for older records.
    const pick = (obj) => obj?.[lang] || obj?.en || "";
    const nameText = pick(b.name) || "Your business";
    const heroTitle = pick(b.hero_title) || nameText;
    const heroSubtitle = pick(b.hero_subtitle) || pick(b.welcome) || "";
    const footerText = pick(b.footer_text) || "";

    const enabledButtons = (b.buttons || []).filter((btn) => btn.enabled !== false);

    const bgImage = b.background_url ? fileUrl(b.background_url) : null;
    const overlay = typeof b.overlay_opacity === "number" ? Math.min(Math.max(b.overlay_opacity, 0), 1) : 0.35;

    const bgSolid = b.background_color || "#0B0B0F";
    const cardBg = b.card_bg_color || "#FFFFFF";
    const cardText = b.card_text_color || "#111111";

    const defaultBtnBg = b.button_bg_color || "#FFFFFF";
    const defaultBtnText = b.button_text_color || "#111111";
    const defaultFeatBg = b.featured_bg_color || "#F5B301";
    const defaultFeatText = b.featured_text_color || "#111111";

    const pageStyle = bgImage
        ? {
              backgroundColor: bgSolid,
              backgroundImage: `url(${bgImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundAttachment: insideFrame ? "scroll" : "fixed",
          }
        : { backgroundColor: bgSolid };

    const openLink = (url) => {
        if (!url) return;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const rootClasses = insideFrame
        ? "relative w-full h-full min-h-full overflow-hidden"
        : "relative w-full public-page-root";

    const Arrow = isRtl ? ChevronLeft : ChevronRight;

    return (
        <div
            className={rootClasses}
            style={{ ...pageStyle, direction: isRtl ? "rtl" : "ltr" }}
            dir={isRtl ? "rtl" : "ltr"}
            data-testid="public-page-root"
        >
            {/* Full-page dark overlay */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{ backgroundColor: `rgba(0,0,0,${overlay})` }}
                aria-hidden="true"
            />

            <div className="relative z-10 mx-auto max-w-md w-full min-h-full flex flex-col">
                {/* --- Hero (text over full-page background) --- */}
                <div className="px-6 pt-8 pb-6 text-center">
                    <div className="flex justify-end mb-3">
                        <button
                            type="button"
                            onClick={() => setLang(lang === "en" ? "ar" : "en")}
                            className="inline-flex items-center gap-1.5 rounded-full bg-white/20 backdrop-blur-md text-white text-[11px] font-semibold px-2.5 py-1 border border-white/25 active:scale-95 transition-transform"
                            data-testid="public-lang-switch"
                        >
                            <Languages className="h-3 w-3" />
                            {lang === "en" ? "AR" : "EN"}
                        </button>
                    </div>
                    <h1
                        className="font-display text-white text-2xl sm:text-3xl font-bold tracking-tight leading-tight"
                        style={{ textShadow: "0 2px 20px rgba(0,0,0,0.6)" }}
                        data-testid="public-hero-title"
                    >
                        {heroTitle}
                    </h1>
                    {heroSubtitle && (
                        <p
                            className="mt-1.5 text-white/85 text-sm leading-snug"
                            style={{ textShadow: "0 1px 12px rgba(0,0,0,0.5)" }}
                            data-testid="public-hero-subtitle"
                        >
                            {heroSubtitle}
                        </p>
                    )}
                </div>

                {/* --- Buttons card --- */}
                <div className="px-3 pb-6">
                    <div
                        className="rounded-[28px] p-3 sm:p-4 shadow-[0_25px_60px_-20px_rgba(0,0,0,0.55)] border border-black/5"
                        style={{ backgroundColor: cardBg, color: cardText }}
                        data-testid="public-card"
                    >
                        <div className="flex flex-col gap-2.5">
                            {enabledButtons.length === 0 && (
                                <div className="text-center text-sm py-6" style={{ color: cardText, opacity: 0.6 }}>
                                    No links yet.
                                </div>
                            )}
                            {enabledButtons.map((btn) => {
                                const featured = !!btn.featured;
                                const bg = btn.bg_color || (featured ? defaultFeatBg : defaultBtnBg);
                                const bg2 = btn.bg_gradient_end || null;
                                const tc = btn.text_color || (featured ? defaultFeatText : defaultBtnText);
                                const style = {
                                    backgroundImage: bg2 ? `linear-gradient(90deg, ${bg}, ${bg2})` : "none",
                                    backgroundColor: bg2 ? "transparent" : bg,
                                    color: tc,
                                };
                                const title = btn.label?.[lang] || btn.label?.en || "";
                                const subtitle = btn.subtitle?.[lang] || btn.subtitle?.en || "";
                                const showArrow = btn.show_arrow !== false;
                                const isLightBg = isLightColor(bg);
                                const arrowClass = tc && isLightColor(tc) === false ? "" : "";
                                return (
                                    <button
                                        key={btn.id}
                                        type="button"
                                        onClick={() => openLink(btn.url)}
                                        className={`group w-full flex items-center gap-3 rounded-2xl border shadow-[0_4px_14px_-6px_rgba(0,0,0,0.25)] active:scale-[0.985] transition-transform text-start ${
                                            featured ? "py-3" : "py-3"
                                        }`}
                                        style={{
                                            ...style,
                                            borderColor: isLightBg ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.12)",
                                        }}
                                        data-testid={`public-link-${btn.icon}`}
                                    >
                                        {/* Icon in white circle */}
                                        <span className="ms-3 shrink-0 h-12 w-12 rounded-full bg-white flex items-center justify-center shadow-[0_2px_10px_rgba(0,0,0,0.15)]">
                                            <ButtonIcon btn={btn} size={26} />
                                        </span>

                                        {/* Text */}
                                        <span className="flex-1 min-w-0 flex flex-col justify-center leading-tight py-0.5">
                                            <span className="text-[17px] font-bold truncate" style={{ color: tc }}>
                                                {title || "\u00A0"}
                                            </span>
                                            {subtitle && (
                                                <span
                                                    className="text-[11px] font-medium truncate"
                                                    style={{ color: tc, opacity: 0.8 }}
                                                >
                                                    {subtitle}
                                                </span>
                                            )}
                                        </span>

                                        {/* Arrow */}
                                        {showArrow && (
                                            <span
                                                className="me-3 shrink-0 h-8 w-8 rounded-full flex items-center justify-center"
                                                style={{
                                                    backgroundColor: isLightBg
                                                        ? "rgba(0,0,0,0.06)"
                                                        : "rgba(255,255,255,0.9)",
                                                }}
                                            >
                                                <Arrow
                                                    className={`h-4 w-4 ${arrowClass}`}
                                                    style={{ color: isLightBg ? "#111" : "#111" }}
                                                />
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {footerText && (
                            <div
                                className="mt-4 pt-2 pb-1 flex items-center justify-center gap-1.5 text-[13px]"
                                style={{ color: cardText, opacity: 0.65 }}
                                data-testid="public-footer-text"
                            >
                                <Heart className="h-3.5 w-3.5" />
                                <span>{footerText}</span>
                            </div>
                        )}
                    </div>

                    <div className="mt-4 text-center text-[10px] uppercase tracking-[0.25em] text-white/50">
                        Powered by DinePulse
                    </div>
                </div>
            </div>
        </div>
    );
}

// Small helper — determines whether a hex color is perceptually light.
function isLightColor(hex) {
    if (!hex || typeof hex !== "string") return false;
    const s = hex.replace("#", "");
    if (s.length !== 3 && s.length !== 6) return false;
    const to255 = (h) => parseInt(h.length === 1 ? h + h : h, 16);
    const r = to255(s.length === 3 ? s[0] : s.slice(0, 2));
    const g = to255(s.length === 3 ? s[1] : s.slice(2, 4));
    const bl = to255(s.length === 3 ? s[2] : s.slice(4, 6));
    // Perceived luminance
    const l = (0.299 * r + 0.587 * g + 0.114 * bl) / 255;
    return l > 0.72;
}
